import { defineStore } from 'pinia'
import { ref } from 'vue'

import { useLocalization } from '@/shared/composables/localization'
import { getAppURL } from '@/shared/utils/electron'
import { useFetchApi } from '@/shared/utils/fetch'

import { BlfDto } from './'

export const useBlfStore = defineStore('blf', () => {
  const blf = ref(new Map<BlfDto['mac'], BlfDto[]>())

  const fetch = async (macAddress: BlfDto['mac']) => {
    try {
      const fetch = useFetchApi()
      const { data } = await fetch.get<BlfDto[]>(getAppURL({ pathName: `/api/blf?mac=${macAddress}` }))

      blf.value.set(macAddress, data)
    } catch (e) {
      console.error(e)

      const { t } = useLocalization()
      throw new Error(t('terminals.failedToFetchFunctionalKeysSettings'))
    }
  }

  const add = (macAddress: string, newBlf: BlfDto) => {
    if (blf.value.has(macAddress)) {
      const blfs = blf.value.get(macAddress)

      if (blfs) {
        blfs.push(newBlf)
      }
    }
  }

  const create = async (blfDto: BlfDto) => {
    try {
      const fetch = useFetchApi()
      const { data }: { data: BlfDto } = await fetch.post(getAppURL({ pathName: '/api/blf' }), blfDto)

      const list = blf.value.get(data.mac)
      if (list) {
        if (data) {
          list.push(data)
        }

        blf.value.set(data.mac, list.filter(item => item.id !== blfDto.id))
      }
    } catch (e) {
      console.error(e)

      const { t } = useLocalization()
      throw new Error(t('terminals.failedToCreateFunctionalKeysSettings'))
    }
  }

  const remove = async (guid: string, macAddress: BlfDto['mac']) => {
    try {
      const fetch = useFetchApi()
      await fetch.delete(getAppURL({ pathName: `/api/blf/${guid}` }))

      if (blf.value.has(macAddress)) {
        const filtered = blf.value.get(macAddress)?.filter(blf => blf.guid !== guid)
        if (filtered) {
          blf.value.set(macAddress, filtered)
        }
      }
    } catch (e) {
      console.error(e)

      const { t } = useLocalization()
      throw new Error(t('terminals.failedToRemoveFunctionalKeysSettings'))
    }
  }

  const removeLocalBlf = (id: BlfDto['id'], macAddress: BlfDto['mac']) => {
    if (blf.value.has(macAddress)) {
      const filtered = blf.value.get(macAddress)?.filter(blf => blf.id !== id)
      if (filtered) {
        blf.value.set(macAddress, filtered)
      }
    }
  }

  const update = async (blfDto: BlfDto) => {
    try {
      const fetch = useFetchApi()
      await fetch.put(getAppURL({ pathName: `/api/blf/${blfDto.guid}` }), blfDto)

      if (blf.value.has(blfDto.mac)) {
        const list = blf.value.get(blfDto.mac)
        if (!list) return

        const index = list?.findIndex((item) => item.guid === blfDto.guid)

        if (index && index >= 0) {
          list[index] = blfDto
        }
      }
    } catch (e) {
      console.error(e)

      const { t } = useLocalization()
      throw new Error(t('terminals.failedToUpdateFunctionalKeysSettings'))
    }
  }

  return {
    blf,
    fetch,
    add,
    removeLocalBlf,
    remove,
    create,
    update,
  }
})
